import React from "react";

/**
 * SecuritySeal Component.
 * Bank-grade security badge displaying AES-256 encryption status, SOC-2 compliance, and Google Cloud hosting info.
 *
 * @component
 * @param {Object} props - React component props.
 * @param {string} [props.variant="default"] - Badge style variant ("default" or "compact").
 * @param {Object} [props.style={}] - Custom inline CSS styles.
 * @returns {JSX.Element} Security seal badge container.
 */
const SecuritySeal = ({ variant = "default", style = {} }) => {
  const isCompact = variant === "compact";

  /* ── Compact: clean single-line strip for sidebar footer ── */
  if (isCompact) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 7,
          padding: "7px 10px",
          borderRadius: 8,
          background: "rgba(255,255,255,0.05)",
          border: "1px solid rgba(255,255,255,0.09)",
          ...style,
        }}
      >
        {/* Shield icon */}
        <div
          style={{
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            fontSize: 12,
            boxShadow: "0 0 8px rgba(16,185,129,0.35)",
          }}
        >
          🛡️
        </div>

        {/* Text group */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.5px",
              color: "#38bdf8",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            256-Bit AES · SOC-2 · ISO-27001
          </div>
          <div
            style={{
              fontSize: 8.5,
              color: "rgba(255,255,255,0.35)",
              marginTop: 1,
              whiteSpace: "nowrap",
            }}
          >
            Powered by Google Cloud
          </div>
        </div>

        {/* Verified badge */}
        <span
          style={{
            background: "#10b981",
            color: "#022c22",
            fontSize: 7.5,
            fontWeight: 800,
            padding: "2px 5px",
            borderRadius: 4,
            flexShrink: 0,
            letterSpacing: "0.3px",
            textTransform: "uppercase",
          }}
        >
          ✓ Verified
        </span>
      </div>
    );
  }

  /* ── Default: full card for login / standalone use ── */
  return (
    <div
      style={{
        background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
        color: "#f8fafc",
        borderRadius: 10,
        padding: "14px 18px",
        border: "1px solid rgba(56, 189, 248, 0.25)",
        boxShadow: "0 4px 20px rgba(15, 23, 42, 0.25)",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        display: "flex",
        alignItems: "center",
        gap: 14,
        maxWidth: 460,
        margin: "0 auto",
        ...style,
      }}
    >
      {/* Shield */}
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          boxShadow: "0 0 16px rgba(16, 185, 129, 0.4)",
          fontSize: 22,
        }}
      >
        🛡️
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.8px",
            color: "#38bdf8",
            textTransform: "uppercase",
            marginBottom: 4,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <span>SecureCheck Encrypted</span>
          <span
            style={{
              background: "#10b981",
              color: "#022c22",
              fontSize: 8,
              fontWeight: 800,
              padding: "2px 6px",
              borderRadius: 4,
              letterSpacing: "0.3px",
            }}
          >
            VERIFIED
          </span>
        </div>

        <div
          style={{
            fontSize: 11.5,
            color: "#e2e8f0",
            fontWeight: 600,
            lineHeight: 1.4,
          }}
        >
          🔒 <b>256-Bit Bank-Grade AES Encryption</b> · SOC-2 / ISO-27001
        </div>

        <div
          style={{
            fontSize: 10.5,
            color: "#94a3b8",
            marginTop: 3,
          }}
        >
          ☁️ Powered by Google Cloud Storage Infrastructure
        </div>
      </div>
    </div>
  );
};

export default SecuritySeal;
